/**
 * `costing.*` wire contract (garment Increment 2, `garment.costing`).
 *
 * The Style File's cost sheet + the BOM it generates at lock. Money crosses as
 * stringified paise (`moneyPaise`); `*Milli` quantities are integers ×1000;
 * `*Bp` are basis points. The procedures sit behind
 * `entitledProcedureFor("garment.costing")` on the server.
 */
import { z } from "zod";
import { uuid, moneyPaise } from "../_common.js";

export const costLineKind = z.enum(["fabric", "trim", "packing", "process"]);
export const costingStatus = z.enum(["draft", "locked"]);

/** Stringified non-negative bigint — a `*Milli` quantity on the wire. */
const qtyMilli = z.string().regex(/^\d+$/, "milli quantity must be a non-negative integer string");

const costLineInput = z.object({
  kind: costLineKind,
  label: z.string().trim().min(1).max(120),
  itemId: uuid.nullable().optional(),
  consumptionMilli: z.number().int().min(0).max(100_000_000),
  uom: z.string().trim().min(1).max(12),
  ratePaisePerUnit: moneyPaise,
  wastageBp: z.number().int().min(0).max(10_000),
});

export const getInput = z.object({ styleId: uuid });

export const saveInput = z.object({
  styleId: uuid,
  samMinutesMilli: z.number().int().min(0).max(100_000_000),
  cmRatePaisePerMin: moneyPaise,
  efficiencyBp: z.number().int().min(1).max(10_000),
  overheadBp: z.number().int().min(0).max(10_000),
  marginBp: z.number().int().min(0).max(9_999),
  lines: z.array(costLineInput).max(200),
});
export const saveOutput = z.object({ id: uuid, styleId: uuid, status: costingStatus });

export const lockInput = z.object({ styleId: uuid });
export const lockOutput = z.object({
  id: uuid,
  styleId: uuid,
  status: costingStatus,
  fobPricePaise: moneyPaise,
  bomLineCount: z.number().int().nonnegative(),
});

const costLineComputed = z.object({
  kind: costLineKind,
  label: z.string(),
  itemId: uuid.nullable(),
  consumptionMilli: z.number().int().nonnegative(),
  uom: z.string(),
  ratePaisePerUnit: moneyPaise,
  wastageBp: z.number().int().nonnegative(),
  perPiecePaise: moneyPaise,
});

const costingBreakdown = z.object({
  lines: z.array(costLineComputed),
  fabricPaise: moneyPaise,
  trimsPaise: moneyPaise,
  packingPaise: moneyPaise,
  processPaise: moneyPaise,
  cmPaise: moneyPaise,
  subtotalBeforeOverheadPaise: moneyPaise,
  overheadPaise: moneyPaise,
  factoryCostPaise: moneyPaise,
  marginPaise: moneyPaise,
  fobPaise: moneyPaise,
});

const bomLine = z.object({
  id: uuid,
  position: z.number().int().positive(),
  kind: costLineKind,
  label: z.string(),
  itemId: uuid.nullable(),
  uom: z.string(),
  perPieceMilli: z.number().int().nonnegative(),
  wastageBp: z.number().int().nonnegative(),
  orderQty: z.number().int().nonnegative(),
  requiredQtyMilli: qtyMilli,
});

export const costingDetail = z.object({
  id: uuid,
  styleId: uuid,
  status: costingStatus,
  currency: z.string(),
  orderQty: z.number().int().nonnegative(),
  samMinutesMilli: z.number().int().nonnegative(),
  cmRatePaisePerMin: moneyPaise,
  efficiencyBp: z.number().int().positive(),
  overheadBp: z.number().int().nonnegative(),
  marginBp: z.number().int().nonnegative(),
  lockedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  breakdown: costingBreakdown,
  bomLines: z.array(bomLine),
});

/** `costing.get` returns `null` until a cost sheet has been saved. */
export const getOutput = costingDetail.nullable();

/**
 * `costing.preview` — the live cost-sheet calculation, no persistence at
 * all. Reuses `saveInput`'s exact shape (`styleId` is present but unused —
 * `computeCosting` needs no tenant-scoped reads) so the SPA can debounce
 * the same payload it would otherwise `save`, and get the identical
 * breakdown shape back instantly.
 */
export const previewInput = saveInput;
export const previewOutput = costingBreakdown;

export type CostLineKind = z.infer<typeof costLineKind>;
export type CostingStatus = z.infer<typeof costingStatus>;
/** The serialized cost-sheet read-model (money + dates as strings). */
export type CostingDetailWire = z.infer<typeof costingDetail>;
export type SaveCostingInputWire = z.infer<typeof saveInput>;
